/**
 * Secret masking for user-facing CLI error output.
 *
 * Masks credential-shaped strings before text reaches a surface a human can read
 * or forward: console (stderr/stdout) and Sentry. The dominant leak vector is a
 * backend (or AuthKit) error-response body interpolated verbatim into an error
 * message — e.g. `ApiError.message`/`.body`, which dozens of commands print.
 *
 * Standalone leaf module — NO imports — so `api-client.ts` can mask inside the
 * `ApiError` constructor without an import cycle (mirrors the frontend
 * `api-error.ts` leaf-module pattern).
 *
 * Named `maskSecretsInMessage`, not `redactSecrets`, because `@wayai/core` uses
 * the latter name for the opposite contract: it decides whether a whole string
 * is a secret and replaces it outright, which suits an observability payload but
 * would flatten a human-readable CLI message into `[REDACTED]`. This one masks
 * *substrings* and preserves the surrounding prose (and the token prefix, so
 * `way_[REDACTED]` still names the credential that failed). Same subject,
 * opposite contracts — one name for both, in one monorepo, would be a trap.
 */

/**
 * Field names whose value is a credential regardless of its shape — the general
 * rule behind the shape-specific patterns. A backend error body that echoes
 * `{"credential":"<anything>"}` leaks no matter what charset the value uses, so
 * match on the key and mask the value wholesale.
 *
 * Matched against a whole EXTRACTED key, `includes`-style (so `access_token` is
 * covered by `token`), rather than spliced into the key patterns as
 * `[A-Za-z0-9_.-]*(?:token|…)[A-Za-z0-9_.-]*`. That shape looks natural and is
 * quadratic: the runs on either side share the alternation's own alphabet, so
 * every keyword occurrence is a viable split and each drives a scan-to-end that
 * fails (~0.9s on a 100KB body of `token.token.…`, ~22s at 500KB — and `ApiError`
 * masks an unbounded `response.text()`).
 *
 * Deliberately omits a bare `auth`: it would match the benign key `author`, and
 * `authorization` carries the same weight without the false positive.
 */
const CREDENTIAL_KEY_RE =
  /token|secret|credential|password|passwd|api[_-]?key|authorization|private[_-]?key|session[_-]?id|cookie|jwt/i;

/**
 * Credential keys whose numeric JSON value can itself be usable authentication
 * material. Suffix matching covers `accessToken`, `client_secret`, and
 * `user_password` while excluding metadata such as `token_expires` and
 * `credential_enabled`.
 */
const NUMERIC_CREDENTIAL_KEY_RE =
  /(?:password|passwd|pin|token|secret|credential|api[_-]?key|authorization|private[_-]?key|session[_-]?id|cookie|jwt)$/i;

/**
 * A value consisting of NOTHING BUT a placeholder a shape rule already wrote. The
 * key rules are a backstop, so they skip such a value to keep the richer
 * placeholder: `token=way_[REDACTED]` names the credential that failed,
 * `token=[REDACTED]` doesn't, and both are equally safe.
 *
 * Anchored (^…$) on purpose. "Value CONTAINS a placeholder" would disable the
 * backstop exactly where it is the only rule that covers the rest: a shape rule
 * masks only its own token, so
 * `{"authorization":"Bearer way_[REDACTED], X-Api-Key: <raw>"}` would keep the
 * placeholder and leak `<raw>`.
 */
const FULLY_MASKED_VALUE_RE =
  /^(?:(?:Bearer|Basic)\s+)?(?:way_|wst_)?\[REDACTED(?:_JWT|_BASE64)?\]$/;

const isCredentialKey = (key: string): boolean => CREDENTIAL_KEY_RE.test(key);

/**
 * Index at which a value starting at `from` ends: the first `isEnd` character that
 * is not itself escaped. So `token=aa\"<tail>` masks whole instead of stopping at
 * the quote.
 *
 * A left-to-right scan where a backslash consumes the NEXT character — NOT "an end
 * character not preceded by a backslash", which is the tempting reading (and what
 * a `(?<!\\)` lookbehind would give). The two differ on `\\`: that is an escaped
 * backslash, so a quote after it is a live terminator. Reading it the other way
 * would run the value past the end of its JSON string and swallow the rest of the
 * body.
 */
function valueEnd(text: string, from: number, isEnd: (char: string) => boolean): number {
  for (let i = from; i < text.length; i++) {
    if (text[i] === '\\') {
      i++;
      continue;
    }
    if (isEnd(text[i])) return i;
  }
  return text.length;
}

/**
 * Mask the value of every credential-named key matched by `keyRe` — which matches
 * the KEY ONLY (group 1), never the value.
 *
 * That split is the whole design, and both halves of it are load-bearing:
 *
 * - The credential list is NOT spelled into `keyRe` (see CREDENTIAL_KEY_RE): a
 *   `[A-Za-z0-9_.-]*(?:token|…)[A-Za-z0-9_.-]*` key is ambiguous and quadratic.
 *   So the pattern matches every key and this decides, which means a DECLINED key
 *   must cost nothing.
 * - Because the value is never part of the match, declining is just
 *   `lastIndex = valueStart` — the value is rescanned for credentials nested
 *   inside it, and a benign key can never swallow one
 *   (`request_url=https://api/x?api_key=<secret>`). Matching the value and
 *   rewinding on decline gets the same answer but rescans a long value at every
 *   step, which is quadratic; not matching it is linear.
 *
 * Scanning the value by hand also means it ends where the CONTEXT says, not where
 * the charset that happened to be convenient says — no masking a prefix and
 * leaking the tail.
 */
function maskCredentialValues(
  text: string,
  keyRe: RegExp,
  resolveValueEnd: (text: string, from: number) => number,
): string {
  keyRe.lastIndex = 0;
  let out = '';
  let copied = 0;
  let match: RegExpExecArray | null;
  while ((match = keyRe.exec(text)) !== null) {
    const valueStart = match.index + match[0].length;
    // Where `exec` already left us, since the match is the key only — restated so
    // the "a declined key consumes nothing" invariant is local to this loop, and
    // survives anyone extending `keyRe`.
    keyRe.lastIndex = valueStart;
    if (!isCredentialKey(match[1])) continue;
    const end = resolveValueEnd(text, valueStart);
    const value = text.slice(valueStart, end);
    // Keep a placeholder a shape rule already wrote; it names the credential.
    if (value.length === 0 || FULLY_MASKED_VALUE_RE.test(value)) continue;
    out += text.slice(copied, valueStart) + '[REDACTED]';
    copied = end;
    keyRe.lastIndex = end;
  }
  return out + text.slice(copied);
}

const isBase64UrlChar = (char: string): boolean => {
  const code = char.charCodeAt(0);
  return (
    (code >= 48 && code <= 57) ||
    (code >= 65 && code <= 90) ||
    (code >= 97 && code <= 122) ||
    char === '-' ||
    char === '_'
  );
};

/**
 * Mask JWT-shaped substrings in one left-to-right pass.
 *
 * Starting a greedy regex at each `eyJ` is quadratic when a long base64url run
 * contains many such prefixes but no dots. Scanning maximal base64url segments
 * makes every character participate in a bounded number of checks while still
 * finding tokens embedded after labels such as `refresh-`, `way_`, or `abc123`.
 */
function maskJwts(text: string): string {
  let out = '';
  let copied = 0;
  let cursor = 0;

  while (cursor < text.length) {
    while (cursor < text.length && !isBase64UrlChar(text[cursor])) cursor++;
    const headerStart = cursor;
    let tokenStart = -1;
    while (cursor < text.length && isBase64UrlChar(text[cursor])) {
      if (
        tokenStart < 0 &&
        text[cursor] === 'e' &&
        text[cursor + 1] === 'y' &&
        text[cursor + 2] === 'J'
      ) {
        tokenStart = cursor;
      }
      cursor++;
    }
    const headerEnd = cursor;
    if (headerStart === headerEnd) continue;

    const hasJwtHeader = tokenStart >= headerStart && tokenStart + 3 < headerEnd;
    if (!hasJwtHeader || text[headerEnd] !== '.') continue;

    const payloadStart = headerEnd + 1;
    cursor = payloadStart;
    while (cursor < text.length && isBase64UrlChar(text[cursor])) cursor++;
    const payloadEnd = cursor;
    if (payloadEnd === payloadStart || text[payloadEnd] !== '.') continue;

    const signatureStart = payloadEnd + 1;
    cursor = signatureStart;
    while (cursor < text.length && isBase64UrlChar(text[cursor])) cursor++;
    if (cursor === signatureStart) continue;

    out += text.slice(copied, tokenStart) + '[REDACTED_JWT]';
    copied = cursor;
  }

  return out + text.slice(copied);
}

/** Shapes recognisable on their own, masked wherever they appear. */
const SHAPE_PATTERNS: ReadonlyArray<readonly [RegExp, string]> = [
  // way_ scoped API tokens (keyring/CI credentials). Leading \b avoids
  // over-redacting benign substrings (e.g. `gateway_config`); the charset is
  // base64url (no `.`) so a trailing sentence period stays outside the match.
  [/\bway_[A-Za-z0-9_-]{8,}/g, 'way_[REDACTED]'],
  // wst_ single-use WebSocket tickets (same opaque base64url format).
  [/\bwst_[A-Za-z0-9_-]{8,}/g, 'wst_[REDACTED]'],
  // Stripe API keys and webhook signing secrets. Keep this aligned with
  // packages/core/src/redact.ts; both modules protect observability sinks.
  [
    /(^|[^A-Za-z0-9])(?:(?:sk|pk|rk)_(?:live|test)_[A-Za-z0-9]{8,}|whsec_[A-Za-z0-9]{8,})/g,
    '$1[REDACTED]',
  ],
  // LLM provider API keys. The captured delimiter is deliberate: `_` may
  // legitimately precede a key, while a letter/digit means the prefix is part
  // of an ordinary identifier (for example `task-...` or `maxai-...`).
  [
    /(^|[^A-Za-z0-9])(?:sk-[A-Za-z0-9_-]{16,}|AIza[A-Za-z0-9_-]{16,}|xai-[A-Za-z0-9_-]{16,})/g,
    '$1[REDACTED]',
  ],
  // Opaque Bearer credentials retain a precision floor. Basic credentials use
  // the validated pass below because a single base64 quad such as `YTpi` is
  // usable, but matching every four-letter word after "Basic" would destroy
  // benign prose.
  [/(Bearer\s+)[A-Za-z0-9._~+/=-]{8,}/gi, '$1[REDACTED]'],
];

const BASIC_CREDENTIAL_RE = /(Basic\s+)([A-Za-z0-9+/]{4,}={0,2})(?![A-Za-z0-9+/=])/gi;

const maskBasicCredential = (match: string, prefix: string, encoded: string): string => {
  try {
    const padded = encoded + '='.repeat((4 - (encoded.length % 4)) % 4);
    return atob(padded).includes(':') ? `${prefix}[REDACTED]` : match;
  } catch {
    return match;
  }
};

/**
 * `"access_token":` opening a value in a serialized JSON body. The value is
 * inspected separately so strings and balanced object/array containers can both
 * be replaced while keeping the body parseable — `ApiError.isRetryable` re-parses
 * `.body`.
 */
const JSON_KEY_RE = /"([A-Za-z0-9_.-]+)"\s*:\s*/g;
const isJsonValueEnd = (char: string): boolean => char === '"';

function jsonContainerEnd(text: string, from: number): number | null {
  const opener = text[from];
  if (opener !== '{' && opener !== '[') return null;

  const stack = [opener];
  let inString = false;
  for (let i = from + 1; i < text.length; i++) {
    const char = text[i];
    if (inString) {
      if (char === '\\') {
        i++;
      } else if (char === '"') {
        inString = false;
      }
      continue;
    }

    if (char === '"') {
      inString = true;
    } else if (char === '{' || char === '[') {
      stack.push(char);
    } else if (char === '}' || char === ']') {
      const expected = char === '}' ? '{' : '[';
      if (stack.pop() !== expected) return null;
      if (stack.length === 0) return i + 1;
    }
  }
  return null;
}

function jsonNumberEnd(text: string, from: number): number | null {
  const match = /^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?/.exec(text.slice(from));
  if (!match) return null;
  const end = from + match[0].length;
  let next = end;
  while (next < text.length && /\s/.test(text[next])) next++;
  return next === text.length || text[next] === ',' || text[next] === '}' || text[next] === ']'
    ? end
    : null;
}

function maskJsonCredentialValues(text: string): string {
  JSON_KEY_RE.lastIndex = 0;
  let out = '';
  let copied = 0;
  let match: RegExpExecArray | null;
  while ((match = JSON_KEY_RE.exec(text)) !== null) {
    const valueStart = match.index + match[0].length;
    JSON_KEY_RE.lastIndex = valueStart;
    const key = match[1];
    const isNumericCredentialKey = NUMERIC_CREDENTIAL_KEY_RE.test(key);
    if (!isCredentialKey(key) && !isNumericCredentialKey) continue;

    if (text[valueStart] === '"') {
      const contentStart = valueStart + 1;
      const end = valueEnd(text, contentStart, isJsonValueEnd);
      const value = text.slice(contentStart, end);
      if (value.length === 0 || FULLY_MASKED_VALUE_RE.test(value)) continue;
      out += text.slice(copied, contentStart) + '[REDACTED]';
      copied = end;
      JSON_KEY_RE.lastIndex = end;
      continue;
    }

    if (text[valueStart] === '{' || text[valueStart] === '[') {
      // Fail closed on a truncated/malformed credential container: a partial
      // response body is still capable of carrying the secret we are masking.
      const end = jsonContainerEnd(text, valueStart) ?? text.length;
      out += text.slice(copied, valueStart) + '"[REDACTED]"';
      copied = end;
      JSON_KEY_RE.lastIndex = end;
      continue;
    }

    if (isNumericCredentialKey) {
      const end = jsonNumberEnd(text, valueStart);
      if (end !== null) {
        out += text.slice(copied, valueStart) + '"[REDACTED]"';
        copied = end;
        JSON_KEY_RE.lastIndex = end;
      }
    }
  }
  return out + text.slice(copied);
}

/**
 * `access_token=` in a query string, form body, OAuth redirect fragment, or bare
 * prose. The separator class carries `"`/`'` so a `key=value` embedded in a JSON
 * or shell-quoted string is reached, `#` for a redirect fragment's first
 * parameter, and `,` for a comma-joined list.
 */
const QUERY_KEY_RE = /(?:^|[?#&;,\s"'])([A-Za-z0-9_.-]+)=/g;

/**
 * A query/form value ends at a true delimiter, or at a bare `"` — which would
 * close the JSON string this may sit inside, and swallowing it would break the
 * parse. `?`, `#`, `,` and `'` are NOT ends: they occur inside real values, and
 * stopping there would mask a prefix and leak the tail (`password=ab?cd<tail>`).
 * Nothing hides behind them either — a declined key never consumes its value.
 */
const isQueryValueEnd = (char: string): boolean =>
  char === '&' || char === ';' || char === '"' || /\s/.test(char);
const queryValueEnd = (text: string, from: number): number =>
  valueEnd(text, from, isQueryValueEnd);

/** A credential-named HTTP-style header embedded in prose or a JSON string. */
const HEADER_KEY_RE = /(?:^|[^A-Za-z0-9_.\/\\-])([A-Za-z0-9_.-]+):[ \t]*/g;

function headerValueEnd(text: string, from: number): number {
  for (let i = from; i < text.length; i++) {
    const char = text[i];
    if (char === '\r' || char === '\n') return i;
    if (char === '\\') {
      let afterSlashes = i;
      while (text[afterSlashes] === '\\') afterSlashes++;
      const slashCount = afterSlashes - i;
      if (slashCount % 2 === 1 && (text[afterSlashes] === 'r' || text[afterSlashes] === 'n')) {
        return i;
      }
      if (slashCount % 2 === 1) afterSlashes++;
      i = afterSlashes - 1;
      continue;
    }
    if (char === '"') {
      let next = i + 1;
      while (next < text.length && /\s/.test(text[next])) next++;
      if (next === text.length || text[next] === ',' || text[next] === '}' || text[next] === ']') {
        return i;
      }
    }
  }
  return text.length;
}

const BASE64_RE =
  // Padded base64 bodies (wrapped DEKs, HMAC secrets, service-account material)
  // appearing outside any credential-named key — the shape the rules above miss.
  //
  // Base64 has no distinguishing charset: a bare `[A-Za-z0-9+/]{20,}` run also
  // matches slash-separated paths (`hubs/production/agents/support`), 64-char DO
  // hex ids, and ordinary long words — all debugging context, not secrets. Three
  // constraints buy the precision instead:
  //   1. Terminal `=` padding, which prose and paths don't carry.
  //   2. Nothing base64-ish after the padding, so `?param=value` (where `=` is a
  //      separator, not padding) is excluded.
  //   3. Not sitting immediately after `?` or `&`, where a run followed by `=` is
  //      a query parameter NAME. Constraints 2 and 3 are separate cases: `=value`
  //      vs. an empty value (`?someLongParamName=&next=1`), which 2 lets through.
  //   4. The quad-length check below, for a run reached by neither 2 nor 3
  //      (`bad xxxxxxxxxxxxxxxxxxxxxx= here`).
  // Runs last so `Bearer <base64>` still collapses to the Bearer placeholder.
  //
  // The trade: unpadded base64 (any input whose byte length is a multiple of 3)
  // is NOT matched here — no marker distinguishes it from prose. It stays covered
  // by the credential-key rules above, which is where such a value realistically
  // reaches this module.
  //
  // The leading lookbehind (NOT \b) is what keeps this linear. `/` and `+` are
  // non-word characters, so \b also fires *inside* a base64 run: on a body like
  // `aaaa/aaaa/aaaa/…` every slash would start a fresh greedy scan of the rest of
  // the string, which is quadratic (~5.5s on a 100KB body, vs ~1.3ms now). "No
  // base64 char precedes this" is both the correct notion of a run's start and
  // O(1) starts per run — and, unlike the JWT rule above, it narrows nothing:
  // base64 embedded mid-run isn't decodable base64 anyway.
  // {22,} + 1-2 pad chars => a 24-char floor, i.e. a 16-byte secret.
  /(?<![A-Za-z0-9+/])(?<![?&])[A-Za-z0-9+/]{22,}={1,2}(?![A-Za-z0-9+/=])/g;

/**
 * Padded base64 is always a whole number of 4-char quads. Checking the length here
 * rather than encoding the arithmetic into the pattern keeps the regex readable
 * (and free of a nested quantifier) while still rejecting a benign near-miss like
 * `bad xxxxxxxxxxxxxxxxxxxxxx= here` (23 chars, not a multiple of 4).
 */
const maskBase64 = (match: string): string =>
  match.length % 4 === 0 ? '[REDACTED_BASE64]' : match;

const MAX_NESTED_JSON_STRING_DEPTH = 10;

function isSerializedJsonContainer(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed[0] !== '{' && trimmed[0] !== '[') return false;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return typeof parsed === 'object' && parsed !== null;
  } catch {
    return false;
  }
}

/**
 * Decode JSON string VALUES in place, apply the whole masker recursively, then
 * re-encode only content that changed. This reaches upstream error bodies nested
 * at any practical encoding depth without parsing/reformatting the surrounding
 * CLI message. Keys are skipped so a credential-shaped property name cannot be
 * rewritten. The depth bound fails safely against adversarial nesting.
 */
function maskNestedJsonStringValues(text: string, depth: number): string {
  let out = '';
  let copied = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] !== '"') continue;
    const contentStart = i + 1;
    const end = valueEnd(text, contentStart, isJsonValueEnd);
    if (end === text.length) break;

    let next = end + 1;
    while (next < text.length && /\s/.test(text[next])) next++;
    if (text[next] === ':') {
      i = end;
      continue;
    }

    const encoded = text.slice(contentStart, end);
    let decoded: string;
    try {
      decoded = JSON.parse(`"${encoded}"`) as string;
    } catch {
      i = end;
      continue;
    }

    // Once the recursion budget is exhausted, an unresolved serialized JSON
    // container may still hide credential keys at a deeper encoding layer. Keep
    // the surrounding message parseable, but fail closed on that entire value.
    const masked =
      depth >= MAX_NESTED_JSON_STRING_DEPTH && isSerializedJsonContainer(decoded)
        ? '[REDACTED]'
        : maskSecretsInMessageAtDepth(decoded, depth + 1);
    if (masked !== decoded) {
      const reencoded = JSON.stringify(masked).slice(1, -1);
      out += text.slice(copied, contentStart) + reencoded;
      copied = end;
    }
    i = end;
  }
  return out + text.slice(copied);
}

/**
 * Replace credential-shaped secrets in `text` with stable placeholders,
 * preserving the surrounding (non-secret) message so the error stays useful.
 * Returns the input unchanged when it contains no credential-shaped strings.
 *
 * Order matters: the shape rules run first so their richer placeholders survive
 * (`way_[REDACTED]` over a bare `[REDACTED]`), and base64 runs last so a
 * `Bearer <base64>` still collapses to the Bearer placeholder.
 */
function maskSecretsInMessageAtDepth(text: string, depth: number): string {
  // JWTs run first so a `Bearer <jwt>` collapses to the JWT placeholder.
  let out = maskJwts(text);
  for (const [pattern, replacement] of SHAPE_PATTERNS) out = out.replace(pattern, replacement);
  out = out.replace(BASIC_CREDENTIAL_RE, maskBasicCredential);
  out = maskJsonCredentialValues(out);
  if (depth <= MAX_NESTED_JSON_STRING_DEPTH) out = maskNestedJsonStringValues(out, depth);
  out = maskCredentialValues(out, QUERY_KEY_RE, queryValueEnd);
  out = maskCredentialValues(out, HEADER_KEY_RE, headerValueEnd);
  return out.replace(BASE64_RE, maskBase64);
}

export function maskSecretsInMessage(text: string): string {
  return maskSecretsInMessageAtDepth(text, 0);
}
